import type { ObraMapItem } from '@obras/shared';
import {
  formatCurrency,
  formatDate,
  formatDuration,
  formatPercent,
  OBRA_STATUS_LABELS,
} from '@obras/shared';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Bell, PanelRightClose, PanelRightOpen, Play } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Badge, ObraStatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CoverImage, CoverPlaceholder } from '../../components/ui/Cover.js';
import { Skeleton } from '../../components/ui/Feedback.js';
import { Lightbox, useLightbox } from '../../components/ui/Lightbox.js';
import { cn } from '../../lib/cn.js';
import { montarAcervo, paraSlides, type ItemDoAcervo } from '../reports/acervo.js';
import { useReports } from '../reports/api.js';
import { VideoEmEspera } from '../reports/VideoEstado.js';
import { Indicador } from './ObraCard.js';
import { CasaDaSituacao } from './SituacaoNoMapa.js';

/**
 * Quantas fotos o painel mostra: as mais recentes. O painel é uma espiada; o
 * acervo inteiro fica na galeria da obra, a um "Visualizar obra" de distância.
 */
const FOTOS_NO_PAINEL = 12;

/** Até onde a fila de entrada dos quadros da galeria vai. */
const QUADROS_ESCALONADOS = 12;

const ROTULO = 'text-[10px] leading-4 font-medium tracking-[0.06em] text-ink-3 uppercase';

function Dado({
  rotulo,
  largo = false,
  children,
}: {
  rotulo: string;
  /** Ocupa as duas colunas: vigência, endereço. */
  largo?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn(largo && 'col-span-2')}>
      <dt className={ROTULO}>{rotulo}</dt>
      <dd className="mt-1 text-[13px] text-ink">{children}</dd>
    </div>
  );
}

function proximaVisita(obra: ObraMapItem): string {
  if (obra.visitStatus === 'nao_se_aplica') return 'Não se aplica';
  if (obra.visitStatus === 'sem_visita' || !obra.nextVisitDueAt) return 'Sem visita registrada';
  return formatDate(obra.nextVisitDueAt);
}

function vigencia(obra: ObraMapItem): string {
  if (!obra.contractStart || !obra.contractEnd) return 'Não informada';
  return `${formatDate(obra.contractStart)} a ${formatDate(obra.contractEnd)}`;
}

function endereco(obra: ObraMapItem): string {
  const lugar = [obra.city, obra.state].filter(Boolean).join('/');
  return [obra.address, lugar].filter(Boolean).join(' — ') || 'Não informado';
}

/**
 * Um quadro da galeria do painel. Sem zoom: sob o ponteiro, a barra laranja
 * cresce do meio no pé do quadro, o gesto do cartão de obra.
 */
function QuadroDaGaleria({
  item,
  onAbrir,
}: {
  item: ItemDoAcervo;
  onAbrir: (origem: DOMRect) => void;
}) {
  const video = item.kind === 'video';

  // Vídeo que ainda transcodifica não abre: o quadro avisa e pergunta ao servidor.
  if (video && item.status && item.status !== 'pronto') {
    return <VideoEmEspera mediaId={item.id} status={item.status} compacto />;
  }

  return (
    <button
      type="button"
      aria-label={`Ver ${video ? 'o vídeo' : 'a foto'} de ${item.reportTitle}`}
      onClick={(event) => onAbrir(event.currentTarget.getBoundingClientRect())}
      className="group relative block aspect-square w-full overflow-hidden bg-sunken"
    >
      {video && !item.thumbnailUrl ? null : (
        <img
          src={video ? item.thumbnailUrl : item.url}
          alt=""
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
        />
      )}

      {video ? (
        <>
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-8 place-items-center bg-black/55 text-white">
              <Play size={14} strokeWidth={2} fill="currentColor" aria-hidden />
            </span>
          </span>
          {item.durationSeconds ? (
            <span className="tabular absolute top-1 right-1 bg-black/65 px-1 py-px text-[10px] font-medium text-white">
              {formatDuration(item.durationSeconds)}
            </span>
          ) : null}
        </>
      ) : null}

      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out-quint group-hover:scale-x-100 group-focus-visible:scale-x-100"
      />
    </button>
  );
}

/**
 * A galeria da obra no painel: as 12 fotos e vídeos mais recentes dos
 * relatórios — o começo do acervo da aba Galeria, na mesma ordem. A foto clicada
 * cresce do quadro até o centro da tela, no visualizador.
 */
function GaleriaDoPainel({ obraId }: { obraId: string }) {
  const relatorios = useReports({ obraId, perPage: 100 });
  const lightbox = useLightbox();
  const [origem, setOrigem] = useState<DOMRect | null>(null);

  const acervo = useMemo(() => montarAcervo(relatorios.data?.items ?? []), [relatorios.data]);
  // O acervo já vem do mais recente para o mais antigo: as primeiras são as últimas.
  const recentes = useMemo(() => acervo.slice(0, FOTOS_NO_PAINEL), [acervo]);

  /*
   * O visualizador só percorre o que se pode ver: vídeo em processamento fica
   * fora da sequência, senão andar para o lado abriria a tela de espera do
   * próprio Bunny, em inglês.
   */
  const visiveis = useMemo(
    () =>
      recentes.filter((item) => item.kind === 'imagem' || !item.status || item.status === 'pronto'),
    [recentes],
  );
  const slides = useMemo(() => paraSlides(visiveis), [visiveis]);
  const posicaoNoVisualizador = useMemo(
    () => new Map(visiveis.map((item, posicao) => [item.id, posicao])),
    [visiveis],
  );

  return (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className={ROTULO}>Galeria</h3>
        {acervo.length > FOTOS_NO_PAINEL ? (
          <span className="text-[12px] text-ink-3">
            as <span className="tabular font-medium text-ink">{FOTOS_NO_PAINEL}</span> mais recentes
            de <span className="tabular font-medium text-ink">{acervo.length}</span>
          </span>
        ) : acervo.length > 0 ? (
          <span className="text-[12px] text-ink-3">
            <span className="tabular font-medium text-ink">{acervo.length}</span>{' '}
            {acervo.length === 1 ? 'item' : 'itens'}
          </span>
        ) : null}
      </div>

      {relatorios.isPending ? (
        <div className="mt-3 grid grid-cols-3 gap-1.5" aria-hidden>
          {Array.from({ length: 6 }, (_, quadro) => (
            <Skeleton key={quadro} className="aspect-square rounded-none" />
          ))}
        </div>
      ) : relatorios.isError ? (
        <p className="mt-3 text-[13px] text-ink-3">
          Não foi possível carregar as fotos desta obra.
        </p>
      ) : acervo.length === 0 ? (
        <p className="mt-3 text-[13px] text-ink-3">
          Nenhuma foto ou vídeo nos relatórios desta obra.
        </p>
      ) : (
        <ul className="mt-3 grid grid-cols-3 gap-1.5">
          {recentes.map((item, posicao) => (
            <motion.li
              key={item.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                ease: EASE_OUT_QUINT,
                delay: Math.min(posicao, QUADROS_ESCALONADOS) * 0.03,
              }}
            >
              <QuadroDaGaleria
                item={item}
                onAbrir={(retangulo) => {
                  const indice = posicaoNoVisualizador.get(item.id);
                  if (indice === undefined) return;
                  setOrigem(retangulo);
                  lightbox.open(indice);
                }}
              />
            </motion.li>
          ))}
        </ul>
      )}

      {acervo.length > FOTOS_NO_PAINEL ? (
        <p className="mt-3 text-[12px] text-ink-3">
          As demais estão na galeria da obra, em Visualizar obra.
        </p>
      ) : null}

      <Lightbox
        items={slides}
        index={lightbox.index}
        origin={origem}
        onClose={lightbox.close}
        onNavigate={lightbox.navigate}
      />
    </>
  );
}

/** O conteúdo de uma obra no painel; troca inteiro quando outra obra é escolhida. */
function ConteudoDoPainel({
  obra,
  tituloId,
  recolherRef,
  onRecolher,
}: {
  obra: ObraMapItem;
  tituloId: string;
  recolherRef: RefObject<HTMLButtonElement | null>;
  onRecolher: () => void;
}) {
  const navigate = useNavigate();
  const hasUpdates = obra.updatesSinceLastView > 0;

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="relative aspect-[16/9] overflow-hidden">
          {obra.coverUrl ? <CoverImage src={obra.coverUrl} /> : <CoverPlaceholder busy={false} />}

          {hasUpdates || obra.status !== 'em_andamento' ? (
            <span className="absolute top-0 left-0 flex">
              {hasUpdates ? (
                <Badge tone="primary" icon={Bell} solid>
                  {obra.updatesSinceLastView}{' '}
                  {obra.updatesSinceLastView === 1 ? 'notificação' : 'notificações'}
                </Badge>
              ) : null}
              {obra.status !== 'em_andamento' ? (
                <ObraStatusBadge status={obra.status} solid />
              ) : null}
            </span>
          ) : null}

          <button
            ref={recolherRef}
            type="button"
            onClick={onRecolher}
            aria-label="Recolher os detalhes da obra"
            title="Recolher"
            className="absolute top-0 right-0 grid size-10 place-items-center bg-black/65 text-white transition-colors duration-100 hover:bg-black/80"
          >
            <PanelRightClose size={18} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        <div className="px-5 pt-4 pb-5">
          <h2 id={tituloId} className="text-[18px] leading-6 font-semibold tracking-tight">
            {obra.name}
          </h2>
          <p className="mt-1.5 flex min-w-0 items-center gap-2 text-[12px] text-ink-3">
            <span className="shrink-0 font-mono">{obra.contractNumber ?? 'sem contrato'}</span>
            {obra.companyName ? (
              <>
                <span aria-hidden>·</span>
                <span className="truncate">{obra.companyName}</span>
              </>
            ) : null}
          </p>
        </div>

        <dl className="grid grid-cols-2 divide-x divide-line border-y border-line">
          <Indicador rotulo="Execução" valor={formatPercent(obra.executionProgress)} />
          <Indicador
            rotulo="Prazo decorrido"
            valor={formatPercent(obra.timeProgress)}
            title="Quanto do prazo de contrato já passou"
          />
        </dl>

        <section className="px-5 py-5">
          <h3 className={ROTULO}>Informações</h3>
          <dl className="mt-3 grid grid-cols-2 gap-x-5 gap-y-4">
            <Dado rotulo="Situação">
              <span className="flex items-center gap-2">
                <CasaDaSituacao status={obra.status} />
                {OBRA_STATUS_LABELS[obra.status]}
              </span>
            </Dado>
            <Dado rotulo="Tipo">{obra.isReform ? 'Reforma ou ampliação' : 'Construção nova'}</Dado>
            <Dado rotulo="Fiscal responsável">{obra.responsibleFiscal?.name ?? 'Sem fiscal'}</Dado>
            <Dado rotulo="Próxima visita">{proximaVisita(obra)}</Dado>
            <Dado rotulo="Valor do contrato">
              <span className="tabular">{formatCurrency(obra.currentValueCents)}</span>
            </Dado>
            <Dado rotulo="Pago">
              <span className="tabular">{formatCurrency(obra.paidValueCents)}</span>
            </Dado>
            <Dado rotulo="Vigência do contrato" largo>
              <span className="tabular">{vigencia(obra)}</span>
            </Dado>
            <Dado rotulo="Endereço" largo>
              {endereco(obra)}
            </Dado>
          </dl>
        </section>

        <section className="border-t border-line px-5 py-5">
          <GaleriaDoPainel obraId={obra.id} />
        </section>
      </div>

      <footer className="shrink-0 border-t border-line p-4">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          icon={<ArrowRight size={16} strokeWidth={1.5} aria-hidden />}
          onClick={() => void navigate({ to: '/obras/$obraId', params: { obraId: obra.id } })}
        >
          Visualizar obra
        </Button>
      </footer>
    </>
  );
}

/**
 * A coluna da obra, à direita do mapa (design.md §8, Mapa de Obras).
 *
 * Faz parte do conteúdo — o mapa encolhe para ela, em vez de ela flutuar por
 * cima. Recolhida, é uma faixa de 44px com o botão "Detalhes da obra", ligado
 * quando há obra escolhida. Abre quando uma obra é escolhida — a casinha no
 * mapa ou a linha da lista — ou pela faixa, com o resumo, as informações de
 * base, a galeria e, no pé, "Visualizar obra". Aberta, acompanha a escolha:
 * outra obra escolhida no mapa ou na lista troca o conteúdo, descendo.
 * Recolhe no botão do canto da capa ou com Esc.
 *
 * Não pede a ficha da obra à API: abrir a ficha marca a obra como vista e
 * zeraria as notificações, e a coluna é uma espiada, não a visita. Tudo o que
 * ela mostra veio com o mapa; a galeria vem dos relatórios, que só se leem.
 */
export function PainelDaObra({
  obra,
  aberto,
  onAbrir,
  onRecolher,
}: {
  /** A obra escolhida no mapa; sem ela, a coluna fica recolhida e a faixa, desligada. */
  obra: ObraMapItem | null;
  aberto: boolean;
  onAbrir: () => void;
  onRecolher: () => void;
}) {
  const tituloId = useId();
  const recolherRef = useRef<HTMLButtonElement>(null);
  const exibindo = aberto && obra !== null;

  // Ao abrir, o foco vai para a coluna, e o leitor de tela anuncia a obra.
  useEffect(() => {
    if (exibindo) recolherRef.current?.focus({ preventScroll: true });
  }, [exibindo]);

  return (
    <section
      aria-labelledby={exibindo ? tituloId : undefined}
      aria-label={exibindo ? undefined : 'Detalhes da obra'}
      className={cn(
        // A largura anima, e o mapa ao lado acompanha: é conteúdo, não camada.
        'relative order-2 shrink-0 flex-col overflow-hidden border-t border-line bg-surface transition-[width] duration-300 ease-out-quint lg:order-3 lg:border-t-0 lg:border-l',
        exibindo ? 'flex lg:w-[420px]' : 'hidden lg:flex lg:w-11',
      )}
    >
      {exibindo ? (
        /*
         * Outra obra escolhida com a coluna aberta: o conteúdo desce. O da obra
         * anterior sai descendo e se apagando, e o da nova chega de cima,
         * descendo até o lugar — um movimento só, para baixo. A largura do
         * conteúdo é a final desde o começo, para o texto não se rearrumar
         * enquanto a coluna abre.
         */
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={obra.id}
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.28, ease: EASE_OUT_QUINT } }}
            exit={{ opacity: 0, y: 24, transition: { duration: 0.18, ease: EASE_OUT_QUINT } }}
            className="flex min-h-0 w-full flex-1 flex-col lg:w-[420px]"
          >
            <ConteudoDoPainel
              obra={obra}
              tituloId={tituloId}
              recolherRef={recolherRef}
              onRecolher={onRecolher}
            />
          </motion.div>
        </AnimatePresence>
      ) : (
        <button
          type="button"
          onClick={onAbrir}
          disabled={obra === null}
          title={obra ? `Detalhes de ${obra.name}` : 'Escolha uma obra no mapa'}
          aria-label={obra ? `Abrir os detalhes de ${obra.name}` : 'Escolha uma obra no mapa'}
          className="flex h-full w-11 flex-col items-center gap-3 py-4 text-ink-3 transition-colors duration-100 enabled:hover:bg-sunken enabled:hover:text-ink disabled:cursor-default disabled:opacity-50"
        >
          <PanelRightOpen size={18} strokeWidth={1.5} aria-hidden />
          <span className="rotate-180 text-[11px] font-medium tracking-[0.06em] whitespace-nowrap uppercase [writing-mode:vertical-rl]">
            Detalhes da obra
          </span>
        </button>
      )}
    </section>
  );
}
